import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { Card, FallbackButton, Screen } from "@/components/Screen";
import { Note } from "@/components/Note";
import { Spinner } from "@/components/Spinner";
import { BackLink } from "@/components/BackLink";
import { CameraCapture } from "@/components/CameraCapture";
import { Field } from "@/components/Field";
import { PhotoSourceButtons } from "@/screens/onboarding/kyc/shared";
import { KYC_KINDS, friendlyError, kycApi, type KycKind, type KycStatus, type PassportProfileIn } from "@/lib/api";

type Props = {
  onNext: (status: KycStatus) => void;
  onBack: () => void;
  rejectionReason?: string | null;
  cheat?: boolean;
  initialProfile?: PassportProfileIn | null; // префилл ранее введённых данных (повтор после RED)
};

const KIND_META: Record<KycKind, { title: string; sub: string; emoji: string; selfie?: boolean }> = {
  passport_spread: {
    title: "Разворот паспорта",
    sub: "Главная страница с фото и данными. Поместите разворот целиком, без бликов.",
    emoji: "📄",
  },
  registration: {
    title: "Страница с пропиской",
    sub: "Страница со штампом регистрации по месту жительства.",
    emoji: "🏠",
  },
  selfie_with_passport: {
    title: "Селфи с паспортом",
    sub: "Сфотографируйте себя, держа в руке открытый разворот с фото.",
    emoji: "🤳",
    selfie: true,
  },
};

const PASSPORT_FIELDS: { key: string; label: string }[] = [
  { key: "full_name", label: "ФИО" },
  { key: "dob", label: "Дата рождения" },
  { key: "series", label: "Серия" },
  { key: "number", label: "Номер" },
  { key: "issued_by", label: "Кем выдан" },
  { key: "issued_at", label: "Дата выдачи" },
  { key: "division_code", label: "Код подразделения" },
];

type StepResult = { ok: boolean; reason?: string | null; parsed?: Record<string, unknown> | null };
type Phase = "data" | "steps" | "submitting" | "submitted";


const EMPTY_FORM = {
  last_name: "",
  first_name: "",
  middle_name: "",
  date_of_birth: "",
  passport_series: "",
  passport_number: "",
  issued_at: "",
  division_code: "",
  // резидентство: "ru" — Россия (по умолчанию, страну не спрашиваем), "other" — клиент
  // вписывает страну сам. У нерезидента документ не в российском формате, поэтому серия
  // необязательна, номер свободный, а кода подразделения у него нет вовсе.
  residency: "ru" as "ru" | "other",
  residency_country: "",
};

// Паспорта разных стран: в машиночитаемой зоне (ICAO 9303) номер — до 9 знаков, в
// национальных форматах бывает длиннее. 20 символов берём с запасом, чтобы поле не резало
// реальный номер, но и не принимало произвольный текст.
const DOC_MAX = 20;

export function KycManual({ onNext, onBack, rejectionReason, cheat, initialProfile }: Props) {
  // чит-режим: только паспорт + селфи (без паспорта в руках), прописка не нужна
  const kinds = useMemo(
    () => (cheat ? KYC_KINDS.filter((k) => k !== "registration") : KYC_KINDS),
    [cheat],
  );
  const [stepIdx, setStepIdx] = useState(0);
  // сначала клиент вводит данные паспорта — это источник истины, фото потом сверяется с ними
  const [phase, setPhase] = useState<Phase>("data");
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    last_name: initialProfile?.last_name ?? EMPTY_FORM.last_name,
    first_name: initialProfile?.first_name ?? EMPTY_FORM.first_name,
    middle_name: initialProfile?.middle_name ?? EMPTY_FORM.middle_name,
    date_of_birth: initialProfile?.date_of_birth ?? EMPTY_FORM.date_of_birth,
    passport_series: initialProfile?.passport_series ?? EMPTY_FORM.passport_series,
    passport_number: initialProfile?.passport_number ?? EMPTY_FORM.passport_number,
    issued_at: initialProfile?.issued_at ?? EMPTY_FORM.issued_at,
    division_code: initialProfile?.division_code ?? EMPTY_FORM.division_code,
    // пустая страна и «Россия» — оба означают резидента РФ (страну в этом случае не показываем)
    residency: initialProfile?.residency_country && initialProfile.residency_country !== "Россия"
      ? ("other" as const)
      : ("ru" as const),
    residency_country:
      initialProfile?.residency_country && initialProfile.residency_country !== "Россия"
        ? initialProfile.residency_country
        : "",
  }));
  const [savingData, setSavingData] = useState(false);
  const [results, setResults] = useState<Partial<Record<KycKind, StepResult>>>({});
  const [previews, setPreviews] = useState<Partial<Record<KycKind, string>>>({});
  const [busyKind, setBusyKind] = useState<KycKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);


  const kind = kinds[stepIdx];
  // чит-режим: селфи без паспорта в руках — правим текст шага (тип файла тот же)
  const meta =
    cheat && kind === "selfie_with_passport"
      ? { title: "Селфи", sub: "Сфотографируйте своё лицо. Паспорт в руках держать не нужно.", emoji: "🤳", selfie: true }
      : KIND_META[kind];
  const result = results[kind];
  const isLast = stepIdx === kinds.length - 1;
  const busy = busyKind === kind;

  useEffect(() => {
    return () => {
      for (const url of Object.values(previews)) if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePick = useCallback(
    async (k: KycKind, file: File) => {
      const url = URL.createObjectURL(file);
      setPreviews((p) => {
        const prev = p[k];
        if (prev) URL.revokeObjectURL(prev);
        return { ...p, [k]: url };
      });
      setResults((r) => ({ ...r, [k]: undefined }));
      setError(null);
      setBusyKind(k);
      try {
        const res = await kycApi.manualUpload(k, file);
        setResults((r) => ({ ...r, [k]: { ok: res.ok, reason: res.reason, parsed: res.parsed } }));
      } catch (e) {
        setResults((r) => ({ ...r, [k]: { ok: false, reason: friendlyError(e, "Загрузка не удалась") } }));
      } finally {
        setBusyKind(null);
      }
    },
    [],
  );

  const next = useCallback(() => {
    setStepIdx((i) => Math.min(i + 1, kinds.length - 1));
  }, [kinds.length]);

  const submit = useCallback(async () => {
    setPhase("submitting");
    setError(null);
    try {
      const res = await kycApi.manualSubmit();
      if (res.answer === "GREEN") {
        const s = await kycApi.status();
        onNext(s);
        return;
      }
      setPhase("submitted");
    } catch (e) {
      setError(friendlyError(e, "Не удалось отправить"));
      setPhase("steps");
    }
  }, [onNext]);

  const proceed = useCallback(async () => {
    try {
      const s = await kycApi.status();
      onNext(s);
    } catch (e) {
      setError(friendlyError(e));
    }
  }, [onNext]);

  const onlyDigits = (s: string) => s.replace(/\D/g, "");
  const isRu = form.residency === "ru";
  // У резидента РФ формат жёсткий (4 и 6 цифр, код подразделения). У нерезидента документ
  // произвольного формата: серия может отсутствовать, номер — любой в пределах длины,
  // кода подразделения нет — поле не показываем и не требуем.
  const dataValid = Boolean(
    form.last_name.trim() &&
      form.first_name.trim() &&
      form.date_of_birth &&
      form.issued_at &&
      (isRu
        ? onlyDigits(form.passport_series).length === 4 &&
          onlyDigits(form.passport_number).length === 6 &&
          onlyDigits(form.division_code).length === 6
        : form.residency_country.trim().length > 1 &&
          form.passport_number.trim().length > 0 &&
          form.passport_number.trim().length <= DOC_MAX &&
          form.passport_series.trim().length <= DOC_MAX),
  );

  // инлайн-ошибки форматных полей (показываем, только когда поле непустое и неверное)
  const seriesErr =
    isRu && form.passport_series && onlyDigits(form.passport_series).length !== 4 ? "Серия — 4 цифры" : undefined;
  const numberErr =
    isRu && form.passport_number && onlyDigits(form.passport_number).length !== 6 ? "Номер — 6 цифр" : undefined;
  const divErr = form.division_code && onlyDigits(form.division_code).length !== 6 ? "Код — 6 цифр (NNN-NNN)" : undefined;

  const saveData = useCallback(async () => {
    setSavingData(true);
    setError(null);
    try {
      await kycApi.saveProfile({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        middle_name: form.middle_name.trim() || undefined,
        date_of_birth: form.date_of_birth,
        // у нерезидента документ не цифровой — отправляем как введено, только подрезаем длину
        passport_series: isRu
          ? form.passport_series.replace(/\D/g, "")
          : form.passport_series.trim().slice(0, DOC_MAX),
        passport_number: isRu
          ? form.passport_number.replace(/\D/g, "")
          : form.passport_number.trim().slice(0, DOC_MAX),
        issued_at: form.issued_at,
        division_code: isRu ? form.division_code : "",
        residency_country: isRu ? "Россия" : form.residency_country.trim(),
      });
      setPhase("steps");
    } catch (e) {
      setError(friendlyError(e, "Не удалось сохранить данные"));
    } finally {
      setSavingData(false);
    }
  }, [form]);

  const mainBtn = useMemo(() => {
    if (phase === "data")
      return { text: "Продолжить", onClick: saveData, visible: true, active: dataValid, loading: savingData };
    if (phase === "submitted") return { text: "Продолжить", onClick: proceed, visible: true, active: true };
    if (phase === "submitting") return { text: "Отправляем…", onClick: () => {}, visible: true, active: false, loading: true };
    if (result?.ok) {
      return isLast
        ? { text: "Отправить на проверку", onClick: submit, visible: true, active: true }
        : { text: "Продолжить", onClick: next, visible: true, active: true };
    }
    return { text: "Сделайте фото", onClick: () => {}, visible: false, active: false, loading: busy };
  }, [phase, result?.ok, isLast, busy, submit, next, proceed, saveData, dataValid, savingData]);

  useMainButton(mainBtn);
  useBackButton(
    phase === "data"
      ? onBack
      : phase === "steps"
        ? stepIdx > 0
          ? () => setStepIdx((i) => i - 1)
          : () => setPhase("data")
        : undefined,
  );

  if (phase === "submitted") {
    return (
      <Screen title="Готово">
        <Card>
          <div className="text-[15px] font-semibold">Документы отправлены на проверку ✓</div>
          <div className="mt-2 text-[13px] text-white/45">
            Мы проверим документы и подтвердим верификацию. Статус появится в вашем профиле —
            обычно это занимает немного времени.
          </div>
        </Card>
        <FallbackButton text="Продолжить" onClick={proceed} />
      </Screen>
    );
  }

  if (phase === "data") {
    const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));
    return (
      <Screen title="Данные паспорта" subtitle="Введите данные точно как в паспорте — затем сверим с фото">
        <button onClick={onBack} className="mb-4 -mt-2 text-[14px] text-white/45 active:opacity-70">
          ‹ Отмена
        </button>
        <Card>
          <div className="space-y-3">
            {/* Резидентство определяет формат документа: у РФ — серия 4 цифры, номер 6 цифр
                и код подразделения; у остальных стран формат свободный. */}
            <label className="block">
              <span className="mb-1.5 block text-[13px] text-white/45">Резидентство</span>
              <div className="flex items-center gap-2 rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-3.5">
                <select
                  value={form.residency}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, residency: e.target.value as "ru" | "other" }))
                  }
                  className="min-w-0 flex-1 appearance-none bg-transparent text-[16px] text-white outline-none [color-scheme:dark]"
                >
                  <option value="ru">Являюсь резидентом РФ</option>
                  <option value="other">Являюсь резидентом другой страны</option>
                </select>
                <span className="pointer-events-none text-white/40">▾</span>
              </div>
            </label>
            {!isRu && (
              <Field
                label="Страна резидентства"
                placeholder="Напр. Казахстан"
                value={form.residency_country}
                onChange={set("residency_country")}
                maxLength={64}
                autoComplete="off"
              />
            )}

            <Field label="Фамилия" value={form.last_name} onChange={set("last_name")} autoComplete="off" />
            <Field label="Имя" value={form.first_name} onChange={set("first_name")} autoComplete="off" />
            <Field label="Отчество (если есть)" value={form.middle_name} onChange={set("middle_name")} autoComplete="off" />
            <Field label="Дата рождения" type="date" value={form.date_of_birth} onChange={set("date_of_birth")} />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label={isRu ? "Серия" : "Серия (если есть)"}
                inputMode={isRu ? "numeric" : "text"}
                placeholder={isRu ? "0000" : "—"}
                value={form.passport_series}
                onChange={set("passport_series")}
                error={seriesErr}
                maxLength={isRu ? 4 : DOC_MAX}
                autoComplete="off"
              />
              <Field
                label="Номер"
                inputMode={isRu ? "numeric" : "text"}
                placeholder={isRu ? "000000" : "Как в документе"}
                value={form.passport_number}
                onChange={set("passport_number")}
                error={numberErr}
                maxLength={isRu ? 6 : DOC_MAX}
                autoComplete="off"
              />
            </div>
            <Field label="Дата выдачи" type="date" value={form.issued_at} onChange={set("issued_at")} />
            {isRu && (
              <Field label="Код подразделения" inputMode="numeric" placeholder="000-000" value={form.division_code} onChange={set("division_code")} error={divErr} />
            )}
          </div>
        </Card>
        {error && (
          <Note variant="danger" className="mt-4">
            {error}
          </Note>
        )}
        <FallbackButton text={savingData ? "Сохраняем…" : "Продолжить"} onClick={saveData} disabled={!dataValid || savingData} />
      </Screen>
    );
  }

  return (
    <Screen
      title="Верификация личности"
      subtitle={`Шаг ${stepIdx + 1} из ${kinds.length}`}
    >
      <BackLink onClick={onBack} className="mb-4 -mt-2">
        ‹ Отмена
      </BackLink>

      {rejectionReason && (
        <Note variant="danger" className="mb-5">
          <div className="font-medium">Предыдущая заявка отклонена</div>
          <div className="mt-1">{rejectionReason}</div>
          <div className="mt-1">Загрузите документы заново.</div>
        </Note>
      )}

      {/* индикатор шагов */}
      <div className="mb-5 flex gap-2">
        {kinds.map((k, i) => {
          const done = results[k]?.ok;
          const activeStep = i === stepIdx;
          return (
            <div
              key={k}
              className={`h-1.5 flex-1 rounded-full ${
                done ? "bg-emerald-400" : activeStep ? "bg-white/40" : "bg-white/10"
              }`}
            />
          );
        })}
      </div>

      <Card>
        <div className="flex items-start gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand/15 text-xl">
            {meta.emoji}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-semibold">{meta.title}</div>
            <div className="mt-1 text-[13px] text-white/45">{meta.sub}</div>
          </div>
        </div>

        {previews[kind] && (
          <div className="mt-3 overflow-hidden rounded-xl bg-black/20">
            <img src={previews[kind]} alt={kind} className="block max-h-60 w-full object-contain" />
          </div>
        )}

        {busy && (
          <div className="mt-3 flex items-center gap-2 rounded-xl bg-black/20 px-4 py-3 text-[13px] text-white/45">
            <Spinner />
            Проверяем фото…
          </div>
        )}

        {!busy && result && !result.ok && (
          <Note variant="danger" className="mt-3">
            {result.reason || "Фото не подходит. Сделайте новый снимок."}
          </Note>
        )}

        {!busy && result?.ok && (
          <div className="mt-3 rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3">
            <div className="text-[13px] font-medium text-emerald-300">✓ Фото принято</div>
            {kind === "passport_spread" && result.parsed && (
              <div className="mt-2 space-y-1">
                {PASSPORT_FIELDS.map(({ key, label }) => {
                  const v = result.parsed?.[key];
                  if (v == null || v === "") return null;
                  return (
                    <div key={key} className="flex gap-2 text-[12px]">
                      <span className="w-32 shrink-0 text-white/45">{label}</span>
                      <span className="font-medium">{String(v)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <PhotoSourceButtons
          className="mt-3"
          busy={busy}
          onCamera={() => setCameraOpen(true)}
          onFile={(f) => handlePick(kind, f)}
        />
      </Card>

      {cameraOpen && (
        <CameraCapture
          facing={meta.selfie ? "user" : "environment"}
          onClose={() => setCameraOpen(false)}
          onCapture={(f) => {
            setCameraOpen(false);
            handlePick(kind, f);
          }}
        />
      )}

      {error && (
        <Note variant="danger" className="mt-4">
          {error}
        </Note>
      )}

      {result?.ok && (
        <FallbackButton
          text={isLast ? "Отправить на проверку" : "Продолжить"}
          onClick={isLast ? submit : next}
        />
      )}
    </Screen>
  );
}
